import { PushNotifications, Token, ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { fetchAPI } from '../util/fetchAPI';
import { getApiOptions } from '../util/getApiOptions';
import { currentEnvironment } from '../config/environment';

const PUSH_DEVICE_ID_KEY = 'virtoly_push_device_id';

function getOrCreateDeviceId(): string {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(PUSH_DEVICE_ID_KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem(PUSH_DEVICE_ID_KEY, id);
  }
  return id;
}

export class PushNotificationService {
  private static instance: PushNotificationService;
  private isInitialized = false;
  private pendingToken: string | null = null; // Store token if user isn't logged in yet
  private currentToken: string | null = null; // Track current registered token

  private constructor() {}

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  /**
   * Initialize push notifications
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized || !Capacitor.isNativePlatform()) {
      return;
    }

    try {
      // Request permissions
      const permStatus = await PushNotifications.requestPermissions();
      
      if (permStatus.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        await PushNotifications.register();

        // Set up event listeners
        this.setupEventListeners();
        
        this.isInitialized = true;
        console.log('Push notifications initialized successfully');
        
        // For iOS with Firebase, the FCM token comes separately
        // Check immediately if token is already available (might have been set before listeners)
        if (Capacitor.getPlatform() === 'ios') {
          // Check multiple times with increasing delays to catch the token
          this.checkForFCMToken(0);
        }
      } else {
        console.warn('Push notification permissions not granted');
      }
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
    }
  }

  /**
   * Check for FCM token (iOS) - recursive with delays
   */
  private checkForFCMToken(attempt: number): void {
    const maxAttempts = 20;
    // Faster checks initially (token might arrive soon), then slower
    const delays = [0, 200, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000];
    const delay = attempt < delays.length ? delays[attempt] : 2000;
    
    if (attempt >= maxAttempts) {
      console.log('⏹️ Stopped checking for FCM token after', maxAttempts, 'attempts');
      // @ts-ignore
      const finalCheck = (window as any).FCMToken;
      if (finalCheck) {
        console.log('✅ FCM token found on final check:', finalCheck.substring(0, 20) + '...');
        this.registerTokenWithBackend(finalCheck);
      } else {
        console.warn('⚠️ FCM token not found after all attempts.');
        console.warn('💡 The token might be available but not accessible. Make sure:');
        console.warn('   1. You are logged in (required for token registration)');
        console.warn('   2. The app has been restarted after code changes');
        console.warn('   3. Check native logs for "Firebase FCM token:" message');
        console.warn('💡 You can manually register using: window.pushNotificationService.manuallyRegisterToken(token)');
      }
      return;
    }
    
    setTimeout(() => {
      // @ts-ignore
      const fcmToken = (window as any).FCMToken;
      
      if (fcmToken && typeof fcmToken === 'string' && fcmToken.length > 0) {
        if (fcmToken !== this.currentToken) {
          console.log(`✅ FCM token found on window (attempt ${attempt + 1}):`, fcmToken.substring(0, 20) + '...');
          this.registerTokenWithBackend(fcmToken);
          return; // Stop checking once we have it
        } else {
          console.log('ℹ️ FCM token already registered, stopping checks');
          return;
        }
      }
      
      // Log progress every 5 attempts with more details
      if (attempt % 5 === 0 && attempt > 0) {
        // @ts-ignore
        const currentWindowToken = (window as any).FCMToken;
        const authToken = this.getAuthToken();
        console.log(`🔍 Still checking for FCM token... (attempt ${attempt + 1}/${maxAttempts})`);
        console.log(`   - window.FCMToken: ${currentWindowToken ? currentWindowToken.substring(0, 20) + '...' : 'not found'}`);
        console.log(`   - User logged in: ${authToken ? 'yes' : 'no'}`);
        console.log(`   - Current registered token: ${this.currentToken ? this.currentToken.substring(0, 20) + '...' : 'none'}`);
        console.log(`   - Pending token: ${this.pendingToken ? this.pendingToken.substring(0, 20) + '...' : 'none'}`);
      }
      
      // Continue checking
      if (!this.currentToken && !this.pendingToken) {
        this.checkForFCMToken(attempt + 1);
      }
    }, delay);
  }

  /**
   * Manually register FCM token (for testing/debugging)
   */
  public async manuallyRegisterToken(token: string): Promise<void> {
    console.log('🔧 Manually registering FCM token...');
    console.log('Token:', token.substring(0, 20) + '...');
    
    // Check if user is logged in
    const authToken = this.getAuthToken();
    if (!authToken) {
      console.error('❌ Cannot register token - user is not logged in!');
      console.log('💡 Please log in to the app first, then try again.');
      return;
    }
    
    await this.registerTokenWithBackend(token);
  }

  /**
   * Debug: Check current state of push notifications
   */
  public debugState(): void {
    // @ts-ignore
    const windowToken = (window as any).FCMToken;
    const authToken = this.getAuthToken();
    
    console.log('📊 Push Notification Debug State:');
    console.log('  - Initialized:', this.isInitialized);
    console.log('  - Current token:', this.currentToken ? this.currentToken.substring(0, 20) + '...' : 'none');
    console.log('  - Pending token:', this.pendingToken ? this.pendingToken.substring(0, 20) + '...' : 'none');
    console.log('  - window.FCMToken:', windowToken ? windowToken.substring(0, 20) + '...' : 'not found');
    console.log('  - User logged in:', authToken ? 'yes' : 'no');
    console.log('  - Platform:', Capacitor.getPlatform());
    
    if (!authToken) {
      console.warn('⚠️ User is NOT logged in - token registration requires authentication');
    }
    
    if (windowToken && !this.currentToken) {
      console.log('💡 FCM token is available on window but not registered. Try:');
      console.log('   window.pushNotificationService.manuallyRegisterToken(window.FCMToken)');
    }
  }


  /**
   * Set up push notification event listeners
   */
  private setupEventListeners(): void {
    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (token: Token) => {
      console.log('Push registration success, token: ' + token.value);
      this.registerTokenWithBackend(token.value);
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ' + JSON.stringify(error));
    });

    // Show us the notification payload if the app was open when we received the notification
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('Push notification received: ', notification);
      this.handleNotificationReceived(notification);
    });

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log('Push notification action performed', notification);
      this.handleNotificationAction(notification);
    });

    // Listen for FCM token from Firebase Messaging (iOS)
    // The AppDelegate posts this notification when FCM token is received
    if (Capacitor.getPlatform() === 'ios') {
      // Listen for FCM token via window event (bridged from native)
      // We'll also check periodically in case the token arrives before listener is set up
      this.setupFCMTokenListener();
    }
  }

  /**
   * Set up listener for FCM token from Firebase Messaging (iOS)
   */
  private setupFCMTokenListener(): void {
    // Listen for FCM token from Capacitor bridge event
    const handleFCMTokenEvent = (event: any) => {
      try {
        console.log('🎯 FCMTokenReceived event received:', event);
        console.log('Event type:', event.type);
        console.log('Event detail:', event.detail);
        
        // Handle different event formats
        let fcmToken: string | null = null;
        
        if (event.detail) {
          // CustomEvent format
          if (typeof event.detail === 'string') {
            try {
              const parsed = JSON.parse(event.detail);
              fcmToken = parsed?.value || parsed?.token;
            } catch {
              // If parsing fails, treat the string as the token
              fcmToken = event.detail;
            }
          } else {
            // Object format
            fcmToken = event.detail?.value || event.detail?.token;
          }
        } else if (event.value || event.token) {
          // Direct property access
          fcmToken = event.value || event.token;
        }
        
        // If token not in event, check window.FCMToken (set by native code)
        if (!fcmToken) {
          // @ts-ignore
          fcmToken = (window as any).FCMToken;
          if (fcmToken) {
            console.log('✅ FCM token found on window.FCMToken');
          }
        }
        
        if (fcmToken && typeof fcmToken === 'string' && fcmToken.length > 0) {
          console.log('✅ FCM token received via event:', fcmToken.substring(0, 20) + '...');
          this.registerTokenWithBackend(fcmToken);
        } else {
          console.warn('⚠️ FCM token event received but token is invalid:', {
            event,
            windowToken: (window as any).FCMToken,
            hasDetail: !!event.detail,
            detailType: typeof event.detail
          });
        }
      } catch (error) {
        console.error('❌ Error parsing FCM token event:', error, event);
      }
    };

    // Listen for the Capacitor bridge event (multiple ways)
    window.addEventListener('FCMTokenReceived', handleFCMTokenEvent as EventListener);
    
    // Also listen on document (some events bubble)
    document.addEventListener('FCMTokenReceived', handleFCMTokenEvent as EventListener);
    
    // Debug: Log when listeners are set up
    console.log('✅ FCM token listener set up for iOS');
    
    // Check immediately if token is already available (might have been set before listener)
    // @ts-ignore
    const immediateToken = (window as any).FCMToken;
    if (immediateToken && typeof immediateToken === 'string' && immediateToken.length > 0) {
      console.log('✅ FCM token found immediately on window.FCMToken:', immediateToken.substring(0, 20) + '...');
      this.registerTokenWithBackend(immediateToken);
    }
    
    console.log('📋 Checking if user is logged in...');
    const authToken = this.getAuthToken();
    if (authToken) {
      console.log('✅ User is logged in - token will register when found');
    } else {
      console.warn('⚠️ User is NOT logged in - token will be saved as pending');
      console.warn('💡 Log in to the app to register the push token');
    }
  }

  /**
   * Get auth token from localStorage (stored in user object)
   */
  private getAuthToken(): string | null {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.access_token || null;
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
    return null;
  }

  /**
   * Register push token with backend
   */
  private async registerTokenWithBackend(token: string): Promise<void> {
    try {
      const platform = Capacitor.getPlatform();
      
      // Get auth token from localStorage (stored in user object)
      const authToken = this.getAuthToken();
      
      if (!authToken) {
        console.warn('⚠️ No auth token found - saving push token for later registration when user logs in.');
        console.warn('Token will be registered automatically after you log in.');
        // Save the token to register later when user logs in
        this.pendingToken = token;
        return;
      }

      // Skip if token hasn't changed (unless it's a pending token)
      if (this.currentToken === token && !this.pendingToken) {
        console.log('Push token unchanged, skipping registration');
        return;
      }
      
      const deviceId = getOrCreateDeviceId();
      console.log('📤 Registering push token with backend...', {
        platform,
        tokenChanged: this.currentToken !== token,
        tokenPrefix: token.substring(0, 20) + '...',
      });

      const response = await fetchAPI(
        '/api/push-notifications/register',
        getApiOptions('POST', { token, platform, device_id: deviceId, app_environment: currentEnvironment }, authToken)
      );

      if (response.success) {
        console.log('✅ Push token registered with backend successfully');
        this.currentToken = token; // Track the registered token
        this.pendingToken = null; // Clear pending token
      } else {
        console.error('❌ Failed to register push token with backend:', response.response);
        // Keep pending token for retry
        this.pendingToken = token;
      }
    } catch (error) {
      console.error('❌ Error registering push token with backend:', error);
      // Keep pending token for retry
      this.pendingToken = token;
    }
  }

  /**
   * Try to register any pending push token (call this after user logs in)
   */
  public async registerPendingToken(): Promise<void> {
    if (this.pendingToken) {
      console.log('Attempting to register pending push token...');
      await this.registerTokenWithBackend(this.pendingToken);
    } else if (this.isInitialized) {
      // If no pending token but we're initialized, try to get a new token
      console.log('No pending token, requesting new token registration...');
      try {
        await PushNotifications.register();
      } catch (error) {
        console.error('Error re-registering for push notifications:', error);
      }
    }
  }

  /**
   * Handle notification received while app is in foreground
   */
  private handleNotificationReceived(notification: PushNotificationSchema): void {
    // You can show an in-app notification here
    // For now, we'll just log it
    console.log('Notification received in foreground:', notification);
    
    // Dispatch a custom event that your app can listen to
    window.dispatchEvent(new CustomEvent('push-notification-received', {
      detail: notification
    }));
  }

  /**
   * Handle notification tap/action
   */
  private handleNotificationAction(notification: ActionPerformed): void {
    const data = notification.notification.data;
    
    console.log('Notification action performed:', notification);
    
    // Always dispatch the action event so notification count can be refreshed
    // This is important for updating the notification count when user taps notification
    window.dispatchEvent(new CustomEvent('push-notification-action', {
      detail: {
        actionUrl: data?.action_url,
        notificationId: data?.notification_id,
        type: data?.type,
        notification: notification.notification,
      }
    }));
  }

  /**
   * Unregister this device's push token so it stops receiving push. Other devices are unaffected.
   */
  public async unregister(): Promise<void> {
    if (!this.isInitialized && !getOrCreateDeviceId()) {
      return;
    }

    try {
      const authToken = this.getAuthToken();
      const deviceId = getOrCreateDeviceId();

      // Unregister this device with the backend (so only this device is removed)
      if (authToken && deviceId) {
        try {
          await fetchAPI(
            '/api/push-notifications/unregister',
            getApiOptions('POST', { device_id: deviceId, app_environment: currentEnvironment }, authToken)
          );
        } catch (error) {
          console.debug('Could not unregister push token on backend:', error);
        }
      }

      // Unregister with native platform (only if initialized)
      if (this.isInitialized) {
        await PushNotifications.unregister();
        this.isInitialized = false;
      }

      this.currentToken = null;
      this.pendingToken = null;

      // Clear badge count and notification center on logout
      await this.clearBadgeAndNotifications();
    } catch (error) {
      console.error('Error unregistering push notifications:', error);
    }
  }

  /**
   * Clear badge count and notification center notifications
   * Note: Push notifications already in the notification center cannot be programmatically removed
   * (this is an OS limitation), but we can clear the badge and prevent new notifications
   */
  public async clearBadgeAndNotifications(): Promise<void> {
    try {
      const platform = Capacitor.getPlatform();
      
      // Clear badge count (iOS)
      if (platform === 'ios') {
        try {
          const { Badge } = await import('@capawesome/capacitor-badge');
          await Badge.clear();
          console.log('✅ iOS badge cleared');
        } catch (error) {
          console.debug('Could not clear iOS badge:', error);
        }
      }

      // Clear all local notifications (both iOS and Android)
      // Note: This only clears local notifications scheduled by the app, not push notifications
      // Push notifications in the notification center must be cleared manually by the user
      try {
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        // Get all pending notifications and cancel them
        const pending = await LocalNotifications.getPending();
        if (pending && pending.notifications && pending.notifications.length > 0) {
          const ids = pending.notifications.map((n: any) => n.id);
          await LocalNotifications.cancel({ notifications: ids.map((id: number) => ({ id })) });
          console.log(`✅ Cleared ${ids.length} local notifications`);
        }
      } catch (error) {
        console.debug('Could not clear local notifications:', error);
      }
    } catch (error) {
      console.debug('Error clearing badge and notifications:', error);
    }
  }

  /**
   * Check if push notifications are available
   */
  public isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get notification settings
   */
  public async getNotificationSettings(): Promise<any> {
    try {
      return await PushNotifications.checkPermissions();
    } catch (error) {
      console.error('Error checking notification permissions:', error);
      return null;
    }
  }
}

// Export singleton instance
export const pushNotificationService = PushNotificationService.getInstance();

// Expose on window for debugging/manual testing
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.pushNotificationService = pushNotificationService;
  
  // Also expose debug helper
  // @ts-ignore
  window.debugPushNotifications = () => {
    pushNotificationService.debugState();
  };
}
